/**
 * Client `callout` (Leanpub/Markua aside) node — the mirror of the server's
 * canonical definition in api/src/utils/tiptap/tiptapContent.ts (`Callout`).
 *
 * Name, attrs, parseHTML and the `<aside data-callout-type data-icon>` shape
 * MUST match the server so ProseMirror docs round-trip identically (a drift
 * guard test enforces the type list).
 *
 * The icon is rendered STATICALLY in renderHTML (an `<img>` whose src is a
 * data-URI SVG), NOT via a React NodeView. The reader (sceneChapterReader.js)
 * builds its DOM through ProseMirror's DOMSerializer; a React NodeView's icon
 * did not reach that read-only DOM (Robert saw the box + text but no glyph),
 * and a raw inline <svg> in a DOMOutputSpec is created without the SVG
 * namespace and would not render. An <img> is a plain element that renders the
 * SVG in the live editor DOM, in @tiptap/html output, and in the BRNative
 * WebView. See calloutRender.js.
 */
import {Node, mergeAttributes} from '@tiptap/core';

import {calloutInnerSpec} from './calloutRender';

export const Callout = Node.create({
  name: 'callout',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      type: {
        default: 'aside',
        parseHTML: element =>
          element.getAttribute('data-callout-type') || 'aside',
        renderHTML: attributes => ({
          'data-callout-type': attributes.type || 'aside'
        })
      },
      icon: {
        default: null,
        parseHTML: element => element.getAttribute('data-icon') || null,
        renderHTML: attributes =>
          attributes.icon ? {'data-icon': attributes.icon} : {}
      }
    };
  },

  parseHTML() {
    return [
      {
        tag: 'aside[data-callout-type]',
        // Only the body holds content: when re-parsing our own rendered HTML
        // (e.g. copy/paste in the editor), this skips the `<img>` glyph so it
        // is never absorbed as a StarterKit image node. Server/markdown HTML
        // has no `.callout__body` wrapper, so fall back to the aside itself.
        contentElement: (dom) => dom.querySelector('.callout__body') || dom
      }
    ];
  },

  renderHTML({node, HTMLAttributes}) {
    const type = (node.attrs && node.attrs.type) || 'aside';
    const icon = node.attrs && node.attrs.icon;
    return [
      'aside',
      mergeAttributes(HTMLAttributes, {class: `callout callout--${type}`}),
      ...calloutInnerSpec(type, icon)
    ];
  }
});
